import { useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "@/src/hooks/useApiClient";
import { useAppToast } from "@/src/hooks/useAppToast";
import { useTranslations } from "@/src/i18n/i18n";
import type { ReportOutcome } from "@/src/pages/Reports/reports.type";

/**
 * Clôt un signalement.
 *
 * Modèle de hook de **mutation** (cf. `useSignIn`) : `onSuccess` / `onError` de
 * React Query, jamais un `useEffect` sur `[isSuccess, isError]`.
 *
 * Deux choses méritent un mot :
 *
 * - **l'invalidation porte sur le préfixe** `["get", "/api/v1/moderation/…"]`,
 *   sans le troisième segment. La clé d'`openapi-react-query` est
 *   `[méthode, chemin, init]`, et `init` contient le filtre : viser la clé
 *   complète ne rafraîchirait que l'onglet courant, alors qu'une clôture
 *   déplace le signalement d'une file vers une autre — les trois sont fausses ;
 * - **`pendingId`** dit *quel* signalement part, et non « une mutation est en
 *   cours ». C'est ce qui permet de ne bloquer que la carte concernée : avec un
 *   simple `isPending`, les six cartes de l'écran se figeraient ensemble.
 */
export function useResolveReport() {
  const $api = useApiClient();
  const queryClient = useQueryClient();
  const t = useTranslations("reports");
  const { showSuccess, showApiError } = useAppToast();

  const { mutate, isPending, variables } = $api.useMutation(
    "patch",
    "/api/v1/moderation/reports/{id}",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/v1/moderation/reports"],
        });
        showSuccess(t("resolved"));
      },
      onError: (error) => showApiError(t("resolve-error"), error),
    },
  );

  return {
    resolve: (id: number, status: ReportOutcome) =>
      mutate({ params: { path: { id } }, body: { status } }),
    pendingId: isPending ? variables.params.path.id : undefined,
  };
}
